import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import styles from './discount-code.module.scss'
import { IState } from 'src/Interface'
import { StateContext } from '../../AppContext'
import { format } from 'date-fns'
import Map from 'src/components/map/Map'
import Button from 'src/components/button/Button'
import axios from 'axios'
import { baseApiURL } from 'src/utils/utils'
import { toast } from 'react-toastify'
import Spinner from 'src/components/spinners/Spinner'
import { DispatchContext } from 'src/AppContext'
import useCancellablePromise from 'src/utils/useCancellablePromise'
import { gaEvenActions, gaEvent, gaEventCategories } from 'src/utils/ga/ga'
import { gaDiscountCampaignEventActions, gaDiscountCampaingMapper, gaUserMapper } from 'src/utils/ga/gaMapper'

interface IProps {
  close?: any
  discountCodeId: number
}

const DiscountCodeDetails: React.FunctionComponent<IProps> = ({ close, discountCodeId }: IProps) => {
  const [areaCodes, setAreaCodes] = useState([])
  const dispatch = useContext(DispatchContext)
  const [currentDiscountCodeId, setCurrentDiscountCodeId] = useState(discountCodeId)
  const state: IState = useContext(StateContext)
  const { cancellablePromise } = useCancellablePromise()
  const [loading, setLoading] = useState(true)
  const currentIndex: number = state.discountCodes.findIndex((o: any) => o.id === Number(currentDiscountCodeId))
  const discountCode = useMemo(() => state.discountCodes.find(discountCode => discountCode.id === Number(currentDiscountCodeId)), [currentDiscountCodeId, state.discountCodes])

  const statuses = useMemo(() => {
    return {
      PENDING: {
        label: 'Afventer',
        style: 'statusBlue'
      },
      LOCKED_ACTIVE: {
        label: 'Aktiv',
        style: 'statusGreen'
      },
      ACTIVE: {
        label: 'Aktiv',
        style: 'statusGreen'
      },
      EXPIRED: {
        label: 'Udløbet',
        style: 'statusRed'
      },
      CANCELLED: {
        label: 'Udløbet',
        style: 'statusRed'
      },
    }
  }, [])

  useEffect(() => {
    if (state.coordinates && state.coordinates.length) {
      setAreaCodes(
        discountCode?.zipCodesFrom.length ?
          state.coordinates.filter(coordinate => discountCode.zipCodesFrom.find(zipCode => coordinate.nr === zipCode.toString())) :
          state.coordinates
      )
      setLoading(false)
    }
  }, [state.coordinates, discountCode])


  const deleteDiscountCode = useCallback(() => {
    setLoading(true)
    axios.delete(`${baseApiURL()}/discount_codes/${currentDiscountCodeId}`,
      {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then(() => {
        cancellablePromise(axios(`${baseApiURL()}/discount_codes`, {
          headers: {
            Authorization:
              `Bearer ${state.user.accessToken}`
          }
        }).then((result) => {
          gaEvent(gaEventCategories.discounts, gaEvenActions.removed_discount_campaign, gaUserMapper(state), gaDiscountCampaingMapper(gaDiscountCampaignEventActions.remove, discountCode))
          setLoading(false)
          close()
          dispatch({ type: 'SHOW_MODAL', modalType: '' })
          dispatch({ type: 'SET_DISCOUNT_CODES', discountCodes: result.data })
        }).catch((err) => {
          toast(err.response.data.message)
          setLoading(false)
        }))
      }).catch((err: any) => {
        toast(err.response.data.message)
      })
  }, [close, currentDiscountCodeId, dispatch, state.user.accessToken, cancellablePromise, state.discountCodes])


  if (!discountCode || !discountCode.id) {
    return null
  }
  return (
    <>
      {/* <Modal title={modalTitle} show={showEditModal || showAreaCodesModal} discountCode={discountCode} areaCodes={showAreaCodes} close={closeModal} type={modalType} /> */}
      <div className="tw-relative tw-flex-full tw-flex tw-flex-col">
        <header className={`${styles.header} ${styles[statuses[discountCode.status].style]}`}>
          <div className='tw-flex tw-flex-col'>
            <p className='tw-text-xl tw-font-semibold'>{statuses[discountCode.status].label}</p>
            <p>{discountCode.code}</p>
          </div>
          <span className={styles.closeIcon} onClick={() => close('/discountCode')}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </span>
        </header>
        <main className={styles.container}>
          <div className={styles.content}>
            {!loading ?
              <Map polygons={areaCodes} />
              :
              <div className='tw-flex tw-justify-center'>
                <Spinner size='large' modifiers='tw-mb-5' />
              </div>
            }
            <div className='tw-flex tw-mt-4'>
              <div className={styles.statsBox}>
                <h5 className={styles.boxTitle}>
                  {discountCode.stats.tripsNumber}
                </h5>
                <p className={styles.boxDescription}>Indløsninger</p>
              </div>
              <div className={styles.statsBox}>
                <h5 className={styles.boxTitle}>
                  {discountCode.stats.totalIncome} kr.
                </h5>
                <p className={styles.boxDescription}>Total omsætning</p>
              </div>
              <div className={styles.statsBox}>
                <h5 className={styles.boxTitle}>
                  {discountCode.stats.averageIncome} kr.
                </h5>
                <p className={styles.boxDescription}>Genm. omsætning</p>
              </div>
            </div>
            <div className={`${styles.orderInfo}`}>
              <div className={styles.discountCodeText}>
                <p className={styles.label}>Navn</p>
                <p className={styles.labelLarge}>{discountCode.code}</p>
              </div>
              <div className={styles.discountCodeText}>
                <p className={styles.label}>Rabat</p>
                <p className={styles.labelLarge}>{discountCode.percentage}%</p>
              </div>
              <div className={styles.discountCodeText}>
                <p className={styles.label}>Fra lokation</p>
                <div className={styles.labelLarge}>
                  {discountCode.zipCodesFrom.length > 0 ?
                    <>
                      <div className='tw-flex tw-w-full'>
                        <ul className='tw-flex tw-flex-wrap tw-w-full'>
                          {discountCode.zipCodesFrom.map((code, index) => {
                            if (index < 8) {
                              return <li key={index} className={styles.zipCode}>{code}{index !== (discountCode.zipCodesFrom.length - 1) ? ',' : ''}</li>
                            }
                          })}

                        </ul>
                        {discountCode.zipCodesFrom.length > 8 && <span className='tw-mt-auto'>...</span>}
                      </div>
                      {discountCode.zipCodesFrom.length > 8 && <button onClick={() => {
                        dispatch({
                          type: 'SHOW_MODAL', showModal: true,
                          isEditable: false,
                          title: 'Fra lokation',
                          modalType: 'showAreaCodes',
                          areaCodes: discountCode.zipCodesFrom
                        })
                      }} className={styles.seeAllButton}>Se alle
                        <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-4 tw-w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg></button>}
                    </> : '-'}
                </div>
              </div>
              <div className={styles.discountCodeText}>
                <p className={styles.label}>Til lokation</p>
                <div className={styles.labelLarge}>
                  {discountCode.zipCodesTo.length > 0 ?
                    <>
                      <div className='tw-flex tw-w-full'>
                        <ul className='tw-flex tw-flex-wrap tw-w-full'>
                          {discountCode.zipCodesTo.map((code, index) => {
                            if (index < 8) {
                              return <li key={index} className={styles.zipCode}>{code}{index !== (discountCode.zipCodesTo.length - 1) ? ',' : ''}</li>
                            }
                          })}

                        </ul>
                        {discountCode.zipCodesTo.length > 8 && <span className='tw-mt-auto'>...</span>}
                      </div>
                      {discountCode.zipCodesTo.length > 8 && <button className={styles.seeAllButton} onClick={() => {
                        dispatch({
                          type: 'SHOW_MODAL',
                          showModal: true,
                          isEditable: false,
                          modalType: 'showAreaCodes',
                          title: 'Til lokation',
                          areaCodes: discountCode.zipCodesTo
                        })
                      }}>Se alle
                        <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-4 tw-w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg></button>}
                    </> : '-'}
                </div>
              </div>
              <div className={styles.discountCodeText}>
                <p className={styles.label}>Start dato</p>
                <p className={styles.labelLarge}>{format(new Date(discountCode.startDate), 'dd/MM/yyyy')}</p>
              </div>
              <div className={styles.discountCodeText}>
                <p className={styles.label}>Slut dato</p>
                <p className={styles.labelLarge}>{discountCode.expiryDate ? format(new Date(discountCode.expiryDate), 'dd/MM/yyyy') : '-'}</p>
              </div>
            </div>
          </div>
          <aside className={styles.orderDetails}>
            <p>
              <span className={styles.labelSmall}>ID</span>
              {discountCode.id}
            </p>
            <p>
              <span className={styles.labelSmall}>Oprettet</span>
              {format(new Date(discountCode.createdAt), 'dd/MM/yyyy')}
            </p>
            <p>
              <span className={styles.labelSmall}>Udstedet af</span>
              {`${discountCode.issuedFromUser.firstName} ${discountCode.issuedFromUser.lastName}`}
            </p>
            <p>
              <span className={styles.labelSmall}>Attribute ID</span>
              {`${discountCode.issuedFromUser.firstName} ${discountCode.attributeCode}`}
            </p>
          </aside>
        </main>
        <footer className={styles.footer}>
          <div className='tw-flex tw-items-center'>
            <button className={`${styles.navigationButton} ${styles.navigationLeft}`} disabled={!state.discountCodes[currentIndex - 1]} onClick={() => setCurrentDiscountCodeId(state.discountCodes[currentIndex - 1].id)}>
              <svg className='tw-h-6 tw-w-6' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              className={`${styles.navigationButton} ${styles.navigationRight}`}
              disabled={!state.discountCodes[currentIndex + 1]}
              onClick={() => setCurrentDiscountCodeId(state.discountCodes[currentIndex + 1].id)}>
              <svg className='tw-h-6 tw-w-6' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          {discountCode.status !== 'EXPIRED' && discountCode.status !== 'CANCELLED' && <>
            {discountCode.status !== 'LOCKED_ACTIVE' &&
              <Button modifiers='tw-ml-auto' type='secondary' label='Rediger' handleClick={() => dispatch({ type: 'SHOW_MODAL', modalType: 'addDiscountCode', showModal: true, discountCode, showBackDrop: true })} />}
            <Button modifiers={discountCode.status === 'LOCKED_ACTIVE' ? 'tw-ml-auto' : 'tw-ml-2'} type='destructive' label='Deaktivér rabatkode' handleClick={() => dispatch({ type: 'SHOW_MODAL', modalType: 'deleteConfirmation', showModal: true, tittitle: 'Fjern rabatkode', text: '<p>Er du sikker på at du vil fjerne denne rabatkode?</p> <p>Dette kan ikke fortrydes.</p>', buttonText: 'Fjern rabatkode', handleDelete: deleteDiscountCode })} />
          </>}
        </footer>
      </div>
    </>
  )
}

export default DiscountCodeDetails
